import clsx from 'clsx';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertInput from 'components/HubertUI/HubertInput';
import { CircleMinus, CirclePlus } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import type { TMatrixColumn } from '@hubertai/screening-question-types';
import { hasLanguageKeyAndStringValue } from '@hubertai/screening-question-types';
import { ECreateQuestionMode, useQuestionEditorContext } from '../../../context/create-question.context';
import { selectQuestionLanguage, selectQuestionMode } from 'rtk/questionEditorSelector';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import {
  removeMatrixColumnByIndex,
  addMatrixColumnByIndex,
  updateMatrixRowColumnByIndex,
} from 'rtk/Question.Editor.Slice';
import { useTranslation } from 'react-i18next';

interface Props extends ComponentProps<'div'> {
  column: TMatrixColumn;
  formIndex: number;
  columnIndex: number;
}

const FormMatrixColumn = ({ column, formIndex, columnIndex, ...props }: Props) => {
  const { t } = useTranslation('Questionsv2');
  const [label, setLabel] = useState<string>('');
  const { questionId } = useQuestionEditorContext();

  const dispatch = useAppDispatch();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));

  useEffect(() => {
    if (column && column.label && hasLanguageKeyAndStringValue(column.label, questionLanguage)) {
      setLabel(column.label[questionLanguage]);
    } else {
      setLabel('');
    }
  }, [questionLanguage, column]);

  return (
    <div {...props} className={clsx('flex flex-row justify-between', 'hubert-vertical-center', 'w-full', 'my-2')}>
      {mode !== ECreateQuestionMode.InTemplateEdit ? (
        <div className="flex flex-col justify-between">
          <CircleMinus
            className="cursor-pointer"
            onClick={() => {
              dispatch(removeMatrixColumnByIndex({ questionId, formIndex, columnIndex }));
            }}
          />

          <CirclePlus
            className="cursor-pointer"
            onClick={() => {
              dispatch(
                addMatrixColumnByIndex({ questionId, formIndex, columnIndex: columnIndex + 1, questionLanguage }),
              );
            }}
          />
        </div>
      ) : null}

      <div className={clsx('w-full', mode !== ECreateQuestionMode.InTemplateEdit && 'w-[80%]')}>
        <HubertInput
          value={label}
          className=""
          type="text"
          disabled={mode === ECreateQuestionMode.InTemplateEdit}
          placeholder={t('form.matrix.columnTextPlaceholder')}
          onChange={(e) => {
            setLabel(e.target.value);
          }}
          onBlur={(e) => {
            dispatch(
              updateMatrixRowColumnByIndex({
                questionId,
                formIndex,
                fieldName: 'columns',
                fieldIndex: columnIndex,
                updateFieldName: 'label',
                updateFieldValue: e.target.value,
                questionLanguage,
              }),
            );
          }}
        />
      </div>

      {column.value || column.value !== null ? (
        <div className={clsx(mode !== ECreateQuestionMode.InTemplateEdit && 'w-[80%]')}>
          <HubertInput
            defaultValue={column.value ?? 0}
            className=""
            type="number"
            disabled={mode === ECreateQuestionMode.InTemplateEdit}
            placeholder={t('form.matrix.columnValuePlaceholder')}
            onChange={(e) => {
              console.log('e.target.value', e.target.value, typeof e.target.value);
              dispatch(
                updateMatrixRowColumnByIndex({
                  questionId,
                  formIndex,
                  fieldName: 'columns',
                  fieldIndex: columnIndex,
                  updateFieldName: 'value',
                  updateFieldValue: parseInt(e.target.value),
                  questionLanguage,
                }),
              );
            }}
          />
        </div>
      ) : (
        <HubertButton
          size={'xs'}
          variant={'outline-borderless'}
          onClick={() => {
            dispatch(
              updateMatrixRowColumnByIndex({
                questionId,
                formIndex,
                fieldName: 'columns',
                fieldIndex: columnIndex,
                updateFieldName: 'value',
                updateFieldValue: 0,
                questionLanguage,
              }),
            );
          }}
        >
          {t('form.matrix.addValue')}
        </HubertButton>
      )}
    </div>
  );
};

export default FormMatrixColumn;
